import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import MAP from "./components/MAP/MAP";
import VideoList from "./components/VIDEO/VideoList";
import SearchEngine from "./components/SEARCH/SearchEngine";
import Personal from "./components/PERSONAL/Personal";
import TimeBar from "./components/TIMEBAR/TimeBar";
import IntegratedTimeBar from "./components/TIMEBAR/IntegratedTimeBar";
import Tabs from "./components/Tabs/Tabs";
import MapTabs from "./components/MAP/MapTabs";
import { EventProvider } from "./components/TIMEBAR/EventContext";
import { SharedEventsProvider } from "./components/TIMEBAR/SharedEventsContext";
import RightPanel from "./components/PERSONAL/RightPanel";

import { EventContextAdapter } from './compatibility/EventContextAdapter';
import { preloadAllEvents } from "./store/eventCache";
import { setupVideoSync, waitForMainPlayerReady } from "./setupVideoSync";
import { useAppStore } from "./store/appStore";
import { initVideoSafetySystem } from "./utils/videoSafetyUtils";

function App() {
  // Get app state from Zustand store
  const timeframe = useAppStore(state => state.timeframe);
  const updateVideoState = useAppStore(state => state.updateVideoState);
  
  // Tracked objects state
  const [trackedObjects, setTrackedObjects] = useState({
    person: [], // Tracked people
    vehicle: [], // Tracked vehicles
    event: []    // Other tracked objects
  });

  // Define timeframe data
  const timeframeData = {
    "11AM": {
      csvFiles: [
        "/day/map/D1.csv",
        "/day/map/D2.csv",
        "/day/map/D3.csv",
        "/day/map/D4.csv",
        "/day/map/D5.csv",
        "/day/map/D6.csv",
        "/day/map/D7.csv",
        "/day/map/D8.csv",
        "/day/map/D9.csv",
        "/day/map/D10.csv",
      ],
      videoFiles: [
        "/day/videos-low/video1.mp4",
        "/day/videos-low/video2.mp4",
        "/day/videos-low/video3.mp4",
        "/day/videos-low/video4.mp4",
        "/day/videos-low/video5.mp4",
        "/day/videos-low/video6.mp4",
        "/day/videos-low/video7.mp4",
        "/day/videos-low/video8.mp4",
        "/day/videos-low/video9.mp4",
        "/day/videos-low/video10.mp4",
      ],
      highResVideoFiles: [
        "/day/videos/video1.mp4",
        "/day/videos/video2.mp4",
        "/day/videos/video3.mp4",
        "/day/videos/video4.mp4",
        "/day/videos/video5.mp4",
        "/day/videos/video6.mp4",
        "/day/videos/video7.mp4",
        "/day/videos/video8.mp4",
        "/day/videos/video9.mp4",
        "/day/videos/video10.mp4",
      ]
    },
    "11PM": {
      csvFiles: [
        "/night/map/D1.csv",
        "/night/map/D2.csv",
        "/night/map/D3.csv",
        "/night/map/D4.csv",
        "/night/map/D5.csv",
        "/night/map/D6.csv",
        "/night/map/D7.csv",
        "/night/map/D8.csv",
        "/night/map/D9.csv",
        "/night/map/D10.csv",
      ],
      videoFiles: [
        "/night/videos-low/video1.mp4",
        "/night/videos-low/video2.mp4",
        "/night/videos-low/video3.mp4",
        "/night/videos-low/video4.mp4",
        "/night/videos-low/video5.mp4",
        "/night/videos-low/video6.mp4",
        "/night/videos-low/video7.mp4",
        "/night/videos-low/video8.mp4",
        "/night/videos-low/video9.mp4",
        "/night/videos-low/video10.mp4",
      ],
      highResVideoFiles: [
        "/night/videos/video1.mp4",
        "/night/videos/video2.mp4",
        "/night/videos/video3.mp4",
        "/night/videos/video4.mp4",
        "/night/videos/video5.mp4",
        "/night/videos/video6.mp4",
        "/night/videos/video7.mp4",
        "/night/videos/video8.mp4",
        "/night/videos/video9.mp4",
        "/night/videos/video10.mp4",
      ]
    }
  };

  // Define state variables and refs
  const [maxTime, setMaxTime] = useState(0);
  const mapRef = useRef(null);
  const videoListRef = useRef(null);
  const timeBarRef = useRef(null);
  const videoSyncRef = useRef(null);
  
  // Get current timeframe files
  const currentCsvFiles = timeframeData[timeframe].csvFiles;
  const currentVideoFiles = timeframeData[timeframe].videoFiles.slice(0, currentCsvFiles.length);
  const currentHighResVideoFiles = timeframeData[timeframe].highResVideoFiles ? 
    timeframeData[timeframe].highResVideoFiles.slice(0, currentCsvFiles.length) : currentVideoFiles;

    useEffect(() => {
      fetch("/day/hls/robot_event_index.json")
        .then(res => res.json())
        .then(data => {
          window.eventIndex = { day: data, night: {} };
          console.log("✅ robot_event_index.json 已加载");
        });
    }, []);
    
  // Initialize video synchronization system
  useEffect(() => {

    if (!videoSyncRef.current) {
      videoSyncRef.current = setupVideoSync();
    }
    
    return () => {
      if (videoSyncRef.current) {
        videoSyncRef.current();
        videoSyncRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const cleanup = initVideoSafetySystem();
    return cleanup;
  }, []);

  // Handle object tracking
  const handleTrackObject = (trackData) => {
    console.log("App received track data:", trackData);
    
    // Determine object category by label
    let category = 'event'; // Default category
    const label = trackData.label.toLowerCase();
    
    if (label.includes('people') || label.includes('person')) {
      category = 'person';
    } else if (label.includes('car') || label.includes('vehicle') || label.includes('truck')|| label.includes('sedan')) {
      category = 'vehicle'; 
    }
    
    // Update tracked objects state
    setTrackedObjects(prev => {
      if (trackData.isTracking) {
        // Add to tracking list if not already present
        if (!prev[category].some(item => 
          item.eventId === trackData.eventId && item.boxId === trackData.boxId
        )) {
          // If the object has features, update the corresponding tab's state
          if (trackData.features) {
            if (category === 'person') {
              // Update person tab features
              const personTab = document.querySelector('.person-tab');
              if (personTab) {
                const descriptorValues = personTab.querySelectorAll('.person-tab__descriptor-select');
                Object.entries(trackData.features).forEach(([key, value]) => {
                  const select = Array.from(descriptorValues).find(el => 
                    el.getAttribute('data-attribute') === key
                  );
                  if (select) {
                    select.value = value;
                    select.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                });
              }
            } else if (category === 'vehicle') {
              // Update vehicle tab features
              const vehicleTab = document.querySelector('.vehicle-tab');
              if (vehicleTab) {
                const descriptorValues = vehicleTab.querySelectorAll('.vehicle-tab__descriptor-select');
                Object.entries(trackData.features).forEach(([key, value]) => {
                  const select = Array.from(descriptorValues).find(el => 
                    el.getAttribute('data-attribute') === key
                  );
                  if (select) {
                    select.value = value;
                    select.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                });
              }
            }
          }
          
          return {
            ...prev,
            [category]: [...prev[category], trackData]
          };
        }
      } else {
        // Remove from tracking list
        return {
          ...prev,
          [category]: prev[category].filter(item => 
            !(item.eventId === trackData.eventId && item.boxId === trackData.boxId)
          )
        };
      }
      return prev;
    });
    
    // Switch to the appropriate tab
    if (trackData.isTracking) {
      const tabs = document.querySelector('.tabs');
      if (tabs) {
        const tabButtons = tabs.querySelectorAll('.tab-button');
        const targetTab = Array.from(tabButtons).find(button => 
          button.textContent.toLowerCase().includes(category)
        );
        if (targetTab) {
          targetTab.click();
        }
      }
    }
  };

  // Handle time update
  const handleTimeUpdate = (newTime) => {
    // Only handle valid numbers
    if (!isNaN(newTime) && newTime >= 0) {
      // Use Zustand to update time
      updateVideoState({ time: newTime }, 'app');
    }
  };

  // Handle timeframe change
// 在 src/App.js 文件中
const handleTimeframeChange = (newTimeframe) => {
  if (newTimeframe === timeframe) return;
  
  // 重置时间和播放状态
  updateVideoState({ 
    time: 0, 
    isPlaying: false 
  }, 'app');
  
  setMaxTime(0);
  
  // 给组件一点时间准备
  setTimeout(() => {
    // 调用 MapTabs 的 resetTimeframe 方法，这会触发地图重建
    if (mapRef.current && mapRef.current.resetTimeframe) {
      mapRef.current.resetTimeframe(newTimeframe);
    }
    
    if (timeBarRef.current && timeBarRef.current.resetTimeframe) {
      timeBarRef.current.resetTimeframe(newTimeframe);
    }
  }, 50);
};

  // Handle video selection change
  const handleVideoSelectionChange = (videoIndex, isSelected) => {
    // Update Map component
    if (mapRef.current) {
      mapRef.current.updateIconsVisibility(videoIndex, isSelected);
    }
    
    // Update TimeBar component
    if (timeBarRef.current) {
      timeBarRef.current.updateTimelineSelection(videoIndex, isSelected);
    }
  };

  // Process CSV data to find maximum time
  const processCSVData = (data) => {
    const lines = data.split(/\r\n|\n/);
    let startTimestamp = null;
    let maxTimeForFile = 0;

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].split(",");
      if (line.length >= 2) {
        try {
          const dateStr = line[1].trim();
          const timestamp = new Date(dateStr).getTime();

          if (isNaN(timestamp)) {
            console.warn(`Invalid date format at line ${i}: ${dateStr}`);
            continue;
          }

          if (!startTimestamp) startTimestamp = timestamp;

          const adjustedTime = (timestamp - startTimestamp) / 1000;
          maxTimeForFile = Math.max(maxTimeForFile, adjustedTime);
        } catch (error) {
          console.error(`Error processing date at line ${i}:`, error);
        }
      }
    }
    return maxTimeForFile;
  };

  // Preload all events on mount
  useEffect(() => {
    preloadAllEvents();
  }, []);
  
  // Calculate maximum time from CSV files
  useEffect(() => {
    let overallMaxTime = 0;

    const processFiles = async () => {
      for (const csvFile of currentCsvFiles) {
        try {
          const response = await fetch(csvFile);
          const data = await response.text();

          if (data.startsWith("<") || data.includes("<html")) {
            console.error("Invalid CSV format:", csvFile);
            continue;
          }

          const fileMaxTime = processCSVData(data);
          overallMaxTime = Math.max(overallMaxTime, fileMaxTime);
        } catch (error) {
          console.error(`Error processing ${csvFile}:`, error);
        }
      }

      setMaxTime(overallMaxTime);
    };

    processFiles();
  }, [currentCsvFiles]);

  // Sync video selection state between components
  useEffect(() => {
    if (videoListRef.current && timeBarRef.current) {
      // Get VideoList's initial selection state
      const videoSelections = videoListRef.current.getSelectedVideos();
      
      // Apply selection state to timeline
      Object.entries(videoSelections).forEach(([index, isSelected]) => {
        if (timeBarRef.current) {
          timeBarRef.current.updateTimelineSelection(Number(index), isSelected);
        }
      });
    }
  }, [timeframe]);

  // Initialize first video when components are ready
  useEffect(() => {
    const initializeFirstVideo = async () => {
      // Wait for components to mount
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Set initial video if none is set
      const store = useAppStore.getState();
      if (!store.videoUrl && store.selection.activeRobotId === null) {
        console.log('Initializing first video');
        
        // Select first robot
        updateVideoState({
          robotId: 1,
          videoUrl: `/${timeframe === "11AM" ? "day" : "night"}/videos/video1.mp4`,
          time: 0,
          isPlaying: false
        }, 'app_init');
        
        // Wait for video player to be ready
        await waitForMainPlayerReady();
        
        // Start playback
        updateVideoState({ isPlaying: true }, 'app_init');
      }
    };
    
    initializeFirstVideo();
  }, [timeframe, updateVideoState]);

  return (
    <EventProvider>
      <SharedEventsProvider>
        <div className="app">
          {/* Video list */}
          <div className="videoList">
            <VideoList 
              ref={videoListRef} 
              videoFiles={currentVideoFiles}
              highResVideoFiles={currentHighResVideoFiles}
              onVideoSelectionChange={handleVideoSelectionChange}
              onTimeframeChange={handleTimeframeChange}
            />
          </div>

          {/* Left side */}
          <div className="left">
            <div className="map">
              <EventContextAdapter>
                <MapTabs 
                  ref={mapRef} 
                  csvFiles={currentCsvFiles}
                  onTimeUpdate={handleTimeUpdate}
                  onTrackObject={handleTrackObject}
                  timeframe={timeframe}
                />
              </EventContextAdapter>
            </div>

            <div className="time-bar">
              <IntegratedTimeBar
                ref={timeBarRef}
                maxTime={maxTime}
                onTimeUpdate={handleTimeUpdate}
                timeframe={timeframe}
                csvFiles={currentCsvFiles}
              />
            </div>
          </div>

          {/* Right side */}
          <div className="right">
            {/* Upper */}
            <div className="upper">
              <div className="search">
                <Tabs trackedObjects={trackedObjects}/>
              </div>
              <div className="personal">
                {/* <Personal /> */}
              </div>
            </div>

            {/* Lower */}
            <div className="down">
              <RightPanel />
            </div>
          </div>
        </div>
      </SharedEventsProvider>
    </EventProvider>
  );
}

export default App;